/** Booking + class-session request validators (plan 07 §3.2). */
import { z } from 'zod';

export const BookingCreateSchema = z.object({
  classSessionId: z.string().uuid(),
  memberId: z.string().uuid().optional(), // staff-on-behalf; ignored for member tokens
  membershipId: z.string().uuid().optional(), // if omitted, server resolves best-fit membership
  resourceLabel: z.string().max(100).optional(), // preferred reformer slot (CS-04)
  clientEventId: z.string().uuid(), // required for idempotent offline replay (I5)
});
export type BookingCreateInput = z.infer<typeof BookingCreateSchema>;

export const BookingCancelSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type BookingCancelInput = z.infer<typeof BookingCancelSchema>;

export const WaitlistJoinSchema = z.object({
  classSessionId: z.string().uuid(),
  membershipId: z.string().uuid().optional(),
  clientEventId: z.string().uuid(),
});
export type WaitlistJoinInput = z.infer<typeof WaitlistJoinSchema>;

// CS-01: recurrence rule for session generation.
export const RecurrenceRuleSchema = z.object({
  daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1), // 0=Sun..6=Sat
  timeOfDay: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'timeOfDay must be HH:MM (00:00–23:59)'),
  durationMinutes: z.number().int().min(15).max(480),
  seriesStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  seriesEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  maxOccurrences: z.number().int().min(1).max(365).optional(),
});
export type RecurrenceRuleInput = z.infer<typeof RecurrenceRuleSchema>;

export const ClassSessionCreateSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('one_off'),
    classId: z.string().uuid(),
    locationId: z.string().uuid(),
    instructorStaffId: z.string().uuid().optional(),
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    capacity: z.number().int().min(1).max(500),
    room: z.string().max(100).optional(),
    bookingOpensAt: z.string().datetime({ offset: true }).optional(),
    bookingClosesAt: z.string().datetime({ offset: true }).optional(),
    cancelCutoffAt: z.string().datetime({ offset: true }).optional(),
    isPt: z.boolean().default(false),
    equipmentSlots: z.array(z.string().max(100)).optional(), // CS-04
  }),
  z.object({
    mode: z.literal('recurring'),
    classId: z.string().uuid(),
    locationId: z.string().uuid(),
    instructorStaffId: z.string().uuid().optional(),
    capacity: z.number().int().min(1).max(500),
    room: z.string().max(100).optional(),
    recurrenceRule: RecurrenceRuleSchema,
    // booking window expressed as offsets from each session's starts_at:
    bookingOpensMinutesBefore: z.number().int().min(0).optional(),
    bookingClosesMinutesBefore: z.number().int().min(0).optional(),
    cancelCutoffMinutesBefore: z.number().int().min(0).optional(),
    isPt: z.boolean().default(false),
    equipmentSlots: z.array(z.string().max(100)).optional(),
  }),
]);
export type ClassSessionCreateInput = z.infer<typeof ClassSessionCreateSchema>;

export const ClassSessionPatchSchema = z
  .object({
    instructorStaffId: z.string().uuid().optional(),
    capacity: z.number().int().min(1).max(500).optional(),
    room: z.string().max(100).optional(),
    startsAt: z.string().datetime({ offset: true }).optional(),
    endsAt: z.string().datetime({ offset: true }).optional(),
    bookingOpensAt: z.string().datetime({ offset: true }).optional(),
    bookingClosesAt: z.string().datetime({ offset: true }).optional(),
    cancelCutoffAt: z.string().datetime({ offset: true }).optional(),
    status: z.enum(['scheduled', 'cancelled']).optional(), // 'completed' set by system only
  })
  .refine((obj) => Object.keys(obj).length > 0, { message: 'At least one field required' });
export type ClassSessionPatchInput = z.infer<typeof ClassSessionPatchSchema>;

/** Cancel a session: optional staff reason recorded in audit_log. */
export const ClassSessionCancelSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type ClassSessionCancelInput = z.infer<typeof ClassSessionCancelSchema>;
